import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useIsVideoFirstArm } from '#src/app/pages/actions/shared/hooks';
import { isIPhone } from '#src/app/shared/utils/userAgent';

import { useVideoContext } from '../../../../context';

import { useCreateObjectUrl } from './hooks/useCreateObjectUrl';

export const VideoPlayer: React.FC = () => {
	const { createObjectURL } = useCreateObjectUrl();
	const {
		data: { blob },
		actions: { setVideoElement },
	} = useVideoContext();

	const videoRef = useRef<HTMLVideoElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const utilityContext = useUtilityContext();
	const iPhone = isIPhone(utilityContext);
	/*
	 * Two changes hang off this, both variant-only so control's preview is untouched:
	 *
	 *  - the frame is pinned to the 3:4 portrait a phone take is recorded in. Production lets the element
	 *    take the video's own ratio, which reads as a letterboxed landscape crop.
	 *  - the iPhone "click play to preview" label is dropped (VQA, 2026-08-31). It sits over the middle of
	 *    the supporter's own face, and the player already carries native controls saying the same thing.
	 *    It ships in production today, so control keeps it until that is decided separately.
	 */
	const isVideoFirstArm = useIsVideoFirstArm();

	useEffect(() => {
		if (videoRef.current && blob) {
			videoRef.current.src = createObjectURL(blob);
			setVideoElement(videoRef.current);
			setIsPlaying(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blob]);

	return (
		<div className={cn('relative w-full overflow-hidden rounded-lg', { 'aspect-[3/4] bg-black': isVideoFirstArm })}>
			{iPhone && !isPlaying && !isVideoFirstArm && (
				<div className="absolute top-[35%] flex w-full justify-center">
					<Text variant="body" size="medium" className="text-typography-strong">
						<Translate value="corgi.petition_actions.supporter_voice_video.submit.click_to_play" />
					</Text>
				</div>
			)}
			<video
				className={cn('block w-full object-cover', {
					'border-stroke-weak border-2': iPhone && !isPlaying,
					'h-full': isVideoFirstArm,
				})}
				onPlay={() => setIsPlaying(true)}
				muted
				playsInline
				controls
				ref={videoRef}
				data-testid="player-video-element"
			/>
		</div>
	);
};
